import { Component, inject, Input, OnInit, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { lastValueFrom } from 'rxjs';
import { ApiService } from 'services/api.service';
import { formatDatetime } from 'util/date.utility';
import { CardTableComponent, CardTableConfig } from '../../card-table/card-table.component';
import type {
  BackofficeInformationRequestCard,
  InformationRequestChangeListItem
} from 'types/information-request.interface';

// Change types that represent a file add / remove (newValues set = added, oldValues set = removed)
const FILE_CHANGE_TYPES = [
  'CommunicationBackofficeFile',
  'CommunicationFrontofficeFile',
  'Document',
  'OtherResponseFile',
  'ResponseDraftFile',
  'NamejsResponseFile',
  'NoteFile',
  'CalculationFile',
  'InvoiceFile',
  'SendingEventFile'
];

// Section label derived from the change type (file add / remove rows)
const SECTION_BY_CHANGE_TYPE: Record<string, string> = {
  CommunicationBackofficeFile: 'Saziņa ar klientu',
  CommunicationFrontofficeFile: 'Saziņa ar klientu',
  Document: 'Dokumenti',
  OtherResponseFile: 'Atbilde',
  // LNA-1919 item 9: must match enums.informationRequestChangeType.ResponseDraft verbatim, so
  // adding a draft's text and adding its file are named the same way in the event-type column.
  ResponseDraftFile: 'Atbildes sagataves izmaiņas',
  NamejsResponseFile: 'Atbilde',
  SendingEventFile: 'Atbilde',
  NoteFile: 'Apstrādes piezīmes',
  CalculationFile: 'Rēķins',
  InvoiceFile: 'Rēķins'
};

// Section label derived from the file objectType (DocumentDescriptionEdited rows)
const SECTION_BY_OBJECT_TYPE: Record<string, string> = {
  InformationRequestNoteFile: 'Apstrādes piezīmes',
  InformationRequestDocument: 'Dokumenti',
  InformationRequestBoDocument: 'Dokumenti',
  InformationRequestAuthorizationDocument: 'Dokumenti',
  InformationRequestDiscountDocument: 'Dokumenti',
  InformationRequestCommunicationBackoffice: 'Saziņa ar klientu',
  InformationRequestCommunicationFrontoffice: 'Saziņa ar klientu',
  InformationRequestOtherResponseFile: 'Atbilde',
  InformationRequestResponseDraftFile: 'Atbildes sagatave',
  InformationRequestNamejsResponseFile: 'Atbilde',
  InformationRequestSendingEventAttachment: 'Atbilde',
  InformationRequestCalculationFile: 'Rēķins',
  InformationRequestInvoiceFile: 'Rēķins'
};

@Component({
  selector: 'app-ir-process-history',
  standalone: true,
  imports: [CardTableComponent],
  templateUrl: './ir-process-history.component.html',
  styleUrl: './ir-process-history.component.scss'
})
export class IrProcessHistoryComponent implements OnInit {
  @Input() informationRequest!: BackofficeInformationRequestCard;

  #api = inject(ApiService);
  #translate = inject(TranslateService);

  tableConfig: CardTableConfig<InformationRequestChangeListItem> = {
    data: signal([]),
    columns: [
      {
        title: 'pages.viewInfoRequest.processHistory.columns.date',
        getter: (r) => formatDatetime(r.createdOn, false)
      },
      {
        title: 'pages.viewInfoRequest.processHistory.columns.eventType',
        convert: (r) => this.getEventTypeText(r)
      },
      {
        title: 'pages.viewInfoRequest.processHistory.columns.performer',
        propertyName: 'createdBy'
      },
      {
        title: 'pages.viewInfoRequest.processHistory.columns.result',
        customLogic: 'IrChangeResult'
      }
    ],
    headerTitle: 'pages.viewInfoRequest.processHistory.title',
    headerTitleTooltip: 'irProcessHistory'
  };

  async ngOnInit() {
    if (!this.informationRequest?.id) return;

    const response = await lastValueFrom(this.#api.getInformationRequestChanges(this.informationRequest.id));
    const data: InformationRequestChangeListItem[] = [];
    let firstVersionDateTime: Date | undefined;

    for (const r of response) {
      if (r.type === 'FirstSubmittedVersion') {
        firstVersionDateTime = new Date(r.createdOn);
        break;
      }
    }

    for (const r of response) {
      const rowDateTime = new Date(r.createdOn);
      if (firstVersionDateTime && firstVersionDateTime > rowDateTime && r.type !== 'FirstSubmittedVersion') continue;

      const copy = { ...r };

      if (r.type === 'BackofficeStatus') {
        copy.oldValues = copy.oldValues
          ? `pages.service.infoRequest.backofficeStatus.${copy.oldValues}`
          : '';
        copy.newValues = copy.newValues
          ? `pages.service.infoRequest.backofficeStatus.${copy.newValues}`
          : '';
      }

      data.push(copy);
    }

    this.tableConfig.data.set(this.groupRelatedRows(data));
  }

  /**
   * Rows produced by one user action share a groupId (LNA-1969) and come back contiguous,
   * so adjacent rows with the same non-null groupId collapse into one history entry with
   * multiple result lines. Rows with a null groupId (pre-feature data) stay standalone.
   */
  private groupRelatedRows(rows: InformationRequestChangeListItem[]): InformationRequestChangeListItem[] {
    const grouped: (InformationRequestChangeListItem & { groupRows?: InformationRequestChangeListItem[] })[] = [];

    for (const row of rows) {
      const prev = grouped[grouped.length - 1];
      if (row.groupId && prev?.groupId === row.groupId && prev.groupRows) {
        prev.groupRows.push(row);
      } else if (row.groupId) {
        grouped.push({ ...row, groupRows: [row] });
      } else {
        grouped.push(row);
      }
    }

    // A group of one renders exactly like before — unwrap it back to the plain row.
    return grouped.map((r) => (r.groupRows && r.groupRows.length === 1 ? r.groupRows[0] : r));
  }

  // Builds the 'Notikuma tips' text. Grouped rows list the distinct types of their members.
  private getEventTypeText(r: InformationRequestChangeListItem & { groupRows?: InformationRequestChangeListItem[] }): string {
    if (r.groupRows && r.groupRows.length > 1) {
      const texts = r.groupRows.map((row) => this.#translate.instant(this.getSingleEventTypeText(row)));
      return [...new Set(texts)].join('; ');
    }
    return this.getSingleEventTypeText(r);
  }

  // File rows get a section prefix; other types keep the enum label key (translated by the table).
  private getSingleEventTypeText(r: InformationRequestChangeListItem): string {
    if (r.type === 'DocumentDescriptionEdited') {
      const parsed = this.tryParse(r.newValues);
      const section = SECTION_BY_OBJECT_TYPE[parsed?.objectType] ?? 'Dokumenti';
      const fileName = parsed?.fileName ?? '';
      return `${section}, mainīts ${fileName} datnes apraksts`;
    }

    if (FILE_CHANGE_TYPES.includes(r.type)) {
      const section = SECTION_BY_CHANGE_TYPE[r.type] ?? '';
      return r.newValues ? `${section} - Pievienota datne` : `${section} - Datne dzēsta`;
    }

    return `enums.informationRequestChangeType.${r.type}`;
  }

  private tryParse(value: string | null | undefined): any {
    if (!value) return null;
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }
}
